// Each endorsement summary component renders an EndorsementSummaryCardLayout with the appropriate coverage info passed in as props.
// This component handles the shared logic of allowing users to remove and restore endorsements from the summary page, as well as showing/hiding endorsement coverage details.
import { useState } from 'react';
import styled from 'styled-components';
import Checkbox from '../../../elements/Checkbox';
import { CoverageInfo } from '../../AddOns/addOnQuestionMap';
import {
  Card,
  MainCont,
  MoreInfoCaretCont,
  MoreInfoCaret,
  StyledSmoothCollapse,
  StyledTableHeader,
  StyledTableBody,
  InfoBody,
  LimitHeader,
  LimitCell,
} from '../AddOns/styles';
import { useThemeContext } from '../../../elements/theme/CustomThemeProvider';

interface EndorsementSummaryCardLayoutProps {
  label: string;
  coverageInfo: CoverageInfo[];
  onRemove: () => void;
  onRestore: () => void;
}

const EndorsementSummaryCardLayout = ({
  label,
  coverageInfo,
  onRemove,
  onRestore,
}: EndorsementSummaryCardLayoutProps) => {
  const { theme } = useThemeContext();
  const [isChecked, setIsChecked] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleOnCheck = () => {
    if (isChecked) {
      onRemove();
      setIsChecked(false);
      setIsExpanded(false);
    } else {
      setIsChecked(true);
      onRestore();
    }
  };

  return (
    <AddOnCard isChecked={isChecked}>
      <MainCont>
        <CheckboxCont>
          <Checkbox
            onChange={handleOnCheck}
            selected={isChecked}
            ariaLabel={`${isChecked ? 'remove' : 'add'} ${label}`}
          />
        </CheckboxCont>
        <h3>{label}</h3>
        <MoreInfoCaretCont isChecked={isChecked} onClick={() => isChecked && setIsExpanded(!isExpanded)}>
          {isChecked && (
            <MoreInfoCaret
              aria-label={`${isExpanded ? 'hide' : 'show'} ${label} info`}
              src={theme.icons.caret}
              alt="More Info Caret Icon"
              expanded={isExpanded}
            />
          )}
        </MoreInfoCaretCont>
      </MainCont>
      <StyledSmoothCollapse expanded={isExpanded}>
        <StyledTableHeader>
          <thead>
            <tr>
              <th id={`${label}-coverages`}>Coverage</th>
              <LimitHeader id={`${label}-limits`}>Limit</LimitHeader>
            </tr>
          </thead>
        </StyledTableHeader>
        <InfoBody>
          <StyledTableBody>
            <tbody>
              {coverageInfo.map((info: CoverageInfo) => (
                <tr key={`${label}-${info.coverage}`}>
                  <td headers={`${label}-coverages`}>{info.coverage}</td>
                  <LimitCell headers={`${label}-limits`}>{info.limit}</LimitCell>
                </tr>
              ))}
            </tbody>
          </StyledTableBody>
        </InfoBody>
      </StyledSmoothCollapse>
    </AddOnCard>
  );
};

const CheckboxCont = styled.div`
  align-self: center;
  margin-right: 16px;
`;

const AddOnCard = styled(Card)`
  border: 1px solid #d4d4dd;
  @media screen and (max-width: 767px) {
    width: 100%;
    right: initial;
  }
`;

export default EndorsementSummaryCardLayout;
